import { Box, Button, Stack, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useSnackbar } from 'notistack';
import { FormattedMessage, useIntl } from 'react-intl';

import { addLocalePrefix } from 'utils/routes.utils';
import { listScrollGradient } from 'utils/styles.utils';

import { Routes } from 'constants/routes.constants';
import { useLocale } from 'hooks/useLocale';
import { useModalControl } from 'hooks/useModalControl';
import { useAccount, useSignOut } from 'restful/accounts';
import { useTransferAccept } from 'restful/tickets';

import Authentication from 'components/@account/Authentication';
import AccountCard from 'components/@transfer/AccountCard';

interface Props {
  transferId: string;
  transferVersion: number;
  onAcceptTransfer: () => void;
}

const Footer = ({ transferId, transferVersion, onAcceptTransfer }: Props) => {
  const intl = useIntl();
  const { open, onClose, onOpen } = useModalControl();
  const { isSignedIn } = useAccount();
  const { enqueueSnackbar } = useSnackbar();
  const { signOut } = useSignOut();
  const { locale } = useLocale();
  const { acceptTransfer, isLoading: isAcceptingTransfer } =
    useTransferAccept();

  const redirectUrl = addLocalePrefix(locale, Routes.AcceptTransfer);

  const handleLogin = () => {
    onOpen();
  };

  const handleSignOut = () => {
    signOut({ redirectUrl });
  };

  const handleAcceptTransfer = () => {
    if (!transferId) return;
    acceptTransfer(
      { transferId, version: transferVersion },
      {
        onSuccess: () => {
          onAcceptTransfer();
        },
        onError: (error) => {
          Sentry.captureException(error);
          enqueueSnackbar(
            intl.formatMessage(
              { id: 'transfer_tickets.receive.accept.error.alert' },
              { variant: 'error' },
            ),
          );
        },
      },
    );
  };

  return (
    <>
      <Box
        sx={{
          display: 'block',
          position: { xs: 'sticky', md: 'inherit' },
          bottom: { xs: 0, md: 'auto' },
          mx: { xs: -2, md: 0 },
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            py: 3,
            px: 2,
            borderRadius: { xs: 0, md: '16px' },
            border: { xs: 0, md: '1px solid' },
            borderTop: { xs: '1px solid', md: '1px solid' },
            borderColor: { xs: 'divider', md: 'divider' },
            ...listScrollGradient(),
          }}
        >
          {isSignedIn ? (
            <Stack
              sx={{
                gap: 3,
              }}
            >
              <AccountCard />
              <Button
                fullWidth
                color="secondary"
                variant="outlined"
                onClick={handleAcceptTransfer}
                loading={isAcceptingTransfer}
              >
                <FormattedMessage id="transfer_tickets.receive.accept" />
              </Button>
              <Button
                variant="text"
                onClick={handleSignOut}
                disabled={isAcceptingTransfer}
                fullWidth
              >
                <FormattedMessage id="transfer_tickets.receive.switch_account" />
              </Button>
            </Stack>
          ) : (
            <>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  mb: 2,
                }}
              >
                <FormattedMessage id="transfer_tickets.receive.login.title" />
              </Typography>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleLogin}
                sx={{ width: { xs: '100%', md: 'auto' } }}
              >
                <FormattedMessage id="transfer_tickets.receive.login.label" />
              </Button>
            </>
          )}
        </Box>
      </Box>
      <Authentication
        titleIntlId="transfer_tickets.receive.login.title"
        drawer={{
          open,
          onClose,
        }}
        afterSignInUrl={redirectUrl}
        originSourceCategory="TransferAccept"
      />
    </>
  );
};

export default Footer;
